import React, { useCallback, useMemo, useState, useEffect } from "react";
import {
	Box,
	Button,
	Chip,
	FormControlLabel,
	IconButton,
	Switch,
	TextField,
	Typography,
	Tabs,
	Tab,
	Divider,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DescriptionIcon from "@mui/icons-material/Description";
import AddIcon from "@mui/icons-material/Add";
import RefreshIcon from "@mui/icons-material/Refresh";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { useTranslation } from "react-i18next";
import Swal from "sweetalert2";

import { useCompliance } from "../../context/complianceContext";
import {
	createComplianceDocument,
	deleteComplianceDocument,
	getComplianceDocuments,
	updateComplianceDocument,
} from "../../services/complianceService";
import { toast } from "../../context/ToastContext";
import { ASSETS_BASE_URL } from "../../config";
import RichTextEditor from "../RichTextEditor/RichTextEditor";
import Datagrid from "../Datagrid/Datagrid";
import DatagridLoading from "../DatagridLoading/DatagridLoading";
import ModalWrapper from "../ModalWrapper/ModalWrapper";
import UserName from "../UserName/UserName";
import { formatDateTime } from "../../utilities/globalServiceUtils";

const DOC_TABS = [
	{ key: "EULA", labelKey: "LABELS.EULA" },
	{ key: "TERMS", labelKey: "TITLES.TERMS_AND_CONDITIONS" },
	{ key: "PRIVACY", labelKey: "LABELS.PRIVACY_POLICY" },
	{ key: "SUPPORT", labelKey: "LABELS.SUPPORT" },
	{ key: "COOKIE", labelKey: "LABELS.COOKIE_POLICY", fallback: "Cookie Policy" },
];

const PolicyCenter = () => {
	const { t } = useTranslation();
	const { refresh } = useCompliance();
	const [activeType, setActiveType] = useState("EULA");
	const [documents, setDocuments] = useState({});
	const [loading, setLoading] = useState(false);
	const [submitting, setSubmitting] = useState(false);
	const [selectedFile, setSelectedFile] = useState(null);
	const [removeFile, setRemoveFile] = useState(false);
	const [richTextVersion, setRichTextVersion] = useState(0);
	const [formModalOpen, setFormModalOpen] = useState(false);
	const [detailDoc, setDetailDoc] = useState(null);

	const makeEmptyForm = useCallback(
		(type) => ({
			id: null,
			title: "",
			version_label: "",
			external_url: "",
			description_html: "",
			description_text: "",
			sort_order: 0,
			effective_at: new Date().toISOString().slice(0, 10),
			is_active: type !== "EULA",
			file_path: "",
		}),
		[]
	);

	const [formState, setFormState] = useState(makeEmptyForm("EULA"));

	const activeDocuments = useMemo(
		() => documents[activeType] || [],
		[documents, activeType]
	);

	const loadDocuments = useCallback(
		async (type) => {
			setLoading(true);
			try {
				const response = await getComplianceDocuments({ type });
				if (response.status) {
					
					setDocuments((prev) => ({ ...prev, [type]: response.data || [] }));
				} else {
					toast.trigger({
						message: response.message || t("ALERT_MESSAGES.SOMETHING_WENT_WRONG"),
						severity: "error",
					});
				}
			} catch (error) {
				console.error("Failed to fetch compliance documents", error);
				toast.trigger({
					message: t("ALERT_MESSAGES.SOMETHING_WENT_WRONG"),
					severity: "error",
				});
			} finally {
				setLoading(false);
			}
		},
		[t]
	);

	useEffect(() => {
		loadDocuments(activeType);
	}, [activeType, loadDocuments]);

	useEffect(() => {
		const defaults = makeEmptyForm(activeType);
		const sort = activeType === "EULA" ? (documents[activeType]?.length || 0) : 0;
		setFormState({ ...defaults, sort_order: sort });
		setSelectedFile(null);
		setRemoveFile(false);
		setRichTextVersion((prev) => prev + 1);
	}, [activeType, documents, makeEmptyForm]);

	useEffect(() => {
		if (!formModalOpen) {
			setSelectedFile(null);
			setRemoveFile(false);
		}
	}, [formModalOpen]);

	useEffect(() => {
		if (!formModalOpen) {
			setSelectedFile(null);
			setRemoveFile(false);
		}
	}, [formModalOpen]);

	const handleInputChange = (event) => {
		const { name, value } = event.target;
		setFormState((prev) => ({ ...prev, [name]: value }));
	};

	const handleRichTextChange = (value) => {
		setFormState((prev) => ({
			...prev,
			description_html: value.html,
			description_text: value.text,
		}));
	};

	const handleCreate = () => {
		const defaults = makeEmptyForm(activeType);
		const sort = activeType === "EULA" ? (documents[activeType]?.length || 0) : 0;
		setFormState({ ...defaults, sort_order: sort });
		setSelectedFile(null);
		setRemoveFile(false);
		setRichTextVersion((prev) => prev + 1);
		setFormModalOpen(true);
	};

	const handleEdit = (doc) => {
		setFormState({
			id: doc.id,
			title: doc.title || "",
			version_label: doc.version_label || "",
			external_url: doc.external_url || "",
			description_html: doc.description_html || "",
			description_text: doc.description_text || "",
			sort_order: doc.sort_order ?? 0,
			effective_at: doc.effective_at
				? doc.effective_at.slice(0, 10)
				: new Date().toISOString().slice(0, 10),
			is_active: doc.is_active,
			file_path: doc.file_path || "",
		});
		setSelectedFile(null);
		setRemoveFile(false);
		setRichTextVersion((prev) => prev + 1);
		setFormModalOpen(true);
	};

	const resetForm = () => {
		setFormState(makeEmptyForm(activeType));
		setSelectedFile(null);
		setRemoveFile(false);
		setRichTextVersion((prev) => prev + 1);
	};

	const handleDelete = async (doc) => {
		const confirm = await Swal.fire({
			title: t("TITLES.CONFIRM"),
			text: t("UI_TEXT.DELETE_CONFIRMATION"),
			icon: "warning",
			showCancelButton: true,
			confirmButtonText: t("BUTTON.DELETE"),
		});
		if (!confirm.isConfirmed) return;
		try {
			const response = await deleteComplianceDocument(doc.id);
			if (response.status) {
				toast.trigger({ message: t("ALERT_MESSAGES.DOCUMENT_DELETED_SUCCESSFULLY"), severity: "success" });
				loadDocuments(activeType);
				refresh();
				if (formState.id === doc.id) {
					resetForm();
					setFormModalOpen(false);
				}
			} else {
				toast.trigger({
					message: response.message || t("ALERT_MESSAGES.SOMETHING_WENT_WRONG"),
					severity: "error",
				});
			}
		} catch (error) {
			console.error("Failed to delete document", error);
			toast.trigger({
				message: t("ALERT_MESSAGES.SOMETHING_WENT_WRONG"),
				severity: "error",
			});
		}
	};

	const handleSubmit = async () => {
		if (!formState.title?.trim()) {
			toast.trigger({
				message: t("ALERT_MESSAGES.TITLE_REQUIRED"),
				severity: "warning",
			});
			return;
		}

		setSubmitting(true);
		try {
			const formData = new FormData();
			formData.append("doc_type", activeType);
			formData.append("title", formState.title);
			formData.append("version_label", formState.version_label || "");
			formData.append("external_url", formState.external_url || "");
			formData.append("description_html", formState.description_html || "");
			formData.append("description_text", formState.description_text || "");
			formData.append("sort_order", formState.sort_order ?? 0);
			formData.append("effective_at", formState.effective_at || new Date());
			formData.append("is_active", formState.is_active);
			formData.append("remove_file", removeFile);
			if (selectedFile) {
				formData.append("file", selectedFile);
			}

			let response;
			if (formState.id) {
				response = await updateComplianceDocument(formState.id, formData);
			} else {
				response = await createComplianceDocument(formData);
			}

			if (response.status) {
				toast.trigger({
					message: t("ALERT_MESSAGES.SAVED_SUCCESSFULLY"),
					severity: "success",
				});
				loadDocuments(activeType);
				refresh();
				setFormModalOpen(false);
			} else {
				toast.trigger({
					message: response.message || t("ALERT_MESSAGES.SOMETHING_WENT_WRONG"),
					severity: "error",
				});
			}
		} catch (error) {
			console.error("Failed to save compliance document", error);
			toast.trigger({
				message: t("ALERT_MESSAGES.SOMETHING_WENT_WRONG"),
				severity: "error",
			});
		} finally {
			setSubmitting(false);
		}
	};

	const handleMakeActive = async (doc) => {
		try {
			const formData = new FormData();
			formData.append("doc_type", doc.doc_type);
			formData.append("title", doc.title);
			formData.append("version_label", doc.version_label || "");
			formData.append("external_url", doc.external_url || "");
			formData.append("description_html", doc.description_html || "");
			formData.append("description_text", doc.description_text || "");
			formData.append("sort_order", doc.sort_order ?? 0);
			formData.append("effective_at", doc.effective_at || new Date());
			formData.append("is_active", true);
			formData.append("remove_file", false);

			const response = await updateComplianceDocument(doc.id, formData);
			if (response.status) {
				toast.trigger({ message: t("ALERT_MESSAGES.SAVED_SUCCESSFULLY"), severity: "success" });
				loadDocuments(doc.doc_type);
				refresh();
			} else {
				toast.trigger({
					message: response.message || t("ALERT_MESSAGES.SOMETHING_WENT_WRONG"),
					severity: "error",
				});
			}
		} catch (error) {
			console.error("Failed to activate document", error);
			toast.trigger({
				message: t("ALERT_MESSAGES.SOMETHING_WENT_WRONG"),
				severity: "error",
			});
		}
	};

	const renderTable = () => {
		const columns = [
			{ field: "title", headerName: t("LABELS.TITLE"), flex: 2, minWidth: 150 },
			{
				field: "version_label",
				headerName: t("LABELS.VERSION"),
				flex: 0.5,
				minWidth: 120,
				renderCell: (params) => {
					const value = params?.value ?? params?.row?.version_label;
					return value && String(value).trim() ? value : "-";
				},
			},
			{
				field: "is_active",
				headerName: t("TABLE_HEADERS.STATUS"),
				flex: 0.5,
				minWidth: 130,
				renderCell: (params) => (
					<Chip
						size="small"
						label={params.row.is_active ? t("MENU_ITEMS.ACTIVE") : t("MENU_ITEMS.INACTIVE")}
						color={params.row.is_active ? "success" : "default"}
					/>
				),
			},
			{
				field: "updated_at",
				headerName: t("TABLE_HEADERS.UPDATED_DATE"),
				flex: 0.7,
				minWidth: 180,
				valueGetter: (params) => params?.row?.updated_at || params?.row?.created_at,
				renderCell: (params) => {
					const value = params?.value || params?.row?.updated_at || params?.row?.created_at;
					return value ? formatDateTime(value) : "-";
				},
			},
			{
				field: "updated_by_display_name",
				headerName: t("TABLE_HEADERS.UPDATED_BY") || "Updated By",
				flex: 0.8,
				minWidth: 180,
				renderCell: (params) => {
					const name = params?.row?.updated_by_display_name;
					if (!name) {
						return (
							<Typography variant="body2" color="text.secondary">
								-
							</Typography>
						);
					}
					return (
						<Box
							sx={{
								display: "flex",
								alignItems: "flex-start",
								flexDirection: 'column',
								justifyContent: "center",
								columnGap: 1,
								width: "100%",
								height: '100%'
							}}
						>
							<Box sx={{ display: "flex", alignItems: "center", columnGap: 1 }}>

								<UserName
									name={name}
									shortName
									showTooltip
									tooltipText={name}
									cursor="default"
								/>
							</Box>
						</Box>
					);
				},
			},
			{
				field: "actions",
				headerName: t("TABLE_HEADERS.ACTIONS"),
				flex: 1,
				minWidth: 250,
				sortable: false,
				filterable: false,
				align: 'right',
				headerAlign: 'right',
				renderCell: (params) => (
					<Box sx={{ display: "flex", flexDirection: 'column', justifyContent: 'center', alignItems: 'flex-end', height: '100%' }}>
						<Box sx={{ display: "flex", justifyContent: 'flex-end', alignItems: 'center' }}>
							{params.row.doc_type !== "EULA" && !params.row.is_active && (
								<>
									<Button
										size="small"
										variant="outlined"
										startIcon={<CheckCircleIcon fontSize="small" />}
										onClick={(event) => {
											event.preventDefault();
											event.stopPropagation();
											handleMakeActive(params.row);
										}}
									>
										{t("BUTTON.MAKE_ACTIVE") || "Make Active"}
									</Button>
									<Divider variant="middle" orientation="vertical" sx={{ mx: '10px' }} />
								</>
							)}
							<IconButton
								color="primary"
								onClick={(event) => {
									event.preventDefault();
									event.stopPropagation();
									handleEdit(params.row);
								}}
							>
								<EditIcon />
							</IconButton>
							<IconButton
								color="error"
								onClick={(event) => {
									event.preventDefault();
									event.stopPropagation();
									handleDelete(params.row);
								}}
							>
								<DeleteIcon />
							</IconButton>
						</Box>
					</Box>
				),
			},
		];

		const customButtons = [
			{
				label: t("BUTTON.REFRESH"),
				icon: <RefreshIcon fontSize="small" />,
				onClick: () => loadDocuments(activeType),
			},
			{
				label: t("BUTTON.CREATE"),
				icon: <AddIcon fontSize="small" />,
				onClick: handleCreate,
			},
		];

		return (
			<Box>
				<Tabs
					value={activeType}
					onChange={(_, value) => setActiveType(value)}
					sx={{ mb: 1 }}
				>
					{DOC_TABS.map((tab) => (
						<Tab key={tab.key} label={t(tab.labelKey, { defaultValue: tab.fallback })} value={tab.key} />
					))}
				</Tabs>
				{loading ? (
					<DatagridLoading
						message={t('UI_TEXT.LOADING_POLICY_CENTER', { defaultValue: 'Loading policy documents...' })}
						actionButtons={customButtons.length}
						columns={5}
						rows={6}
					/>
				) : (
						<Datagrid
							data={activeDocuments}
							columns={columns}
							rowIdField="id"
							customButtons={customButtons}
							rowClick
							onRowClick={(params) => setDetailDoc(params.row)}
							showSearchBar
							autoHeight
							pageSize={10}
							hideFilterFromColumnMenu
							lockActiveColumnInManageColumns
							preventHidingLastColumn
					/>
				)}
			</Box>
		);
	};

	const renderFileInfo = () => {
		if (selectedFile) {
			return (
				<Typography variant="caption" color="text.secondary">
					{selectedFile.name}
				</Typography>
			);
		}
		if (formState.file_path) {
			return (
				<Box display="flex" alignItems="center" gap={1}>
					<DescriptionIcon fontSize="small" color="action" />
					<a
						href={`${ASSETS_BASE_URL}/${formState.file_path}`}
						target="_blank"
						rel="noopener noreferrer"
					>
						{formState.file_path.split("/").pop()}
					</a>
				</Box>
			);
		}
		return (
			<Typography variant="caption" color="text.secondary">
				{t("UI_TEXT.NO_ATTACHMENTS")}
			</Typography>
		);
	};

	return (
		<Box>

			{renderTable()}

			<ModalWrapper
				open={formModalOpen}
				setOpen={setFormModalOpen}
				onClose={() => setFormModalOpen(false)}
				title={
					formState.id
						? t("LABELS.POLICY_EDIT_TITLE", {
							type: t(
								DOC_TABS.find(tab => tab.key === activeType)?.labelKey,
								{ defaultValue: DOC_TABS.find(tab => tab.key === activeType)?.fallback }
							),
						}) || `Edit ${activeType}`
						: t("LABELS.POLICY_CREATE_TITLE", {
							type: t(
								DOC_TABS.find(tab => tab.key === activeType)?.labelKey,
								{ defaultValue: DOC_TABS.find(tab => tab.key === activeType)?.fallback }
							),
						}) || `Create ${activeType}`
				}
				subtitle={
					activeType === "EULA"
						? t("ITEMS_TEXT.EULA_MULTIPLE_ACTIVE")
						: t("ITEMS_TEXT.SINGLE_ACTIVE_POLICY")
				}
				content={
					<Box display="flex" flexDirection="column" gap={1}>
						<TextField
							fullWidth
							label={t("LABELS.TITLE")}
							name="title"
							value={formState.title}
							onChange={handleInputChange}
							margin="dense"
							size="small"
							required
						/>
						<TextField
							fullWidth
							label={t("LABELS.VERSION")}
							name="version_label"
							value={formState.version_label}
							onChange={handleInputChange}
							margin="dense"
							size="small"
						/>
						<TextField
							fullWidth
							label={t("LABELS.EXTERNAL_URL") || "External URL"}
							name="external_url"
							value={formState.external_url}
							onChange={handleInputChange}
							margin="dense"
							size="small"
						/>
						<TextField
							fullWidth
							label={t("LABELS.EFFECTIVE_DATE") || "Effective Date"}
							name="effective_at"
							type="date"
							value={formState.effective_at}
							onChange={handleInputChange}
							margin="dense"
							size="small"
							InputLabelProps={{ shrink: true }}
						/>
						<TextField
							fullWidth
							label={t("LABELS.SORT_ORDER") || "Sort Order"}
							name="sort_order"
							type="number"
							value={formState.sort_order}
							onChange={handleInputChange}
							margin="dense"
							size="small"
						/>

						<Box my={1}>
							<RichTextEditor
								key={richTextVersion}
								value={formState.description_html}
								onChange={handleRichTextChange}
								height={200}
							/>
						</Box>

						<Box display="flex" flexDirection="column" gap={1}>
							<Button
								variant="outlined"
								size="small"
								startIcon={<CloudUploadIcon />}
								component="label"
							>
								{t("LABELS.UPLOAD_FILE")}
								<input
									type="file"
									hidden
									onChange={(event) => setSelectedFile(event.target.files?.[0] || null)}
								/>
							</Button>
							{renderFileInfo()}
							{formState.file_path && (
								<FormControlLabel
									control={
										<Switch
											size="small"
											checked={removeFile}
											onChange={(event) => setRemoveFile(event.target.checked)}
										/>
									}
									label={t("UI_TEXT.REMOVE_EXISTING_FILE") || "Remove existing file"}
								/>
							)}
						</Box>

						<FormControlLabel
							control={
								<Switch
									name="is_active"
									checked={formState.is_active}
									onChange={(event) =>
										setFormState((prev) => ({ ...prev, is_active: event.target.checked }))
									}
								/>
							}
							label={t("LABELS.ACTIVE_VERSION") || "Active version"}
						/>
					</Box>
				}
				actions={
					<>
						<Button onClick={() => setFormModalOpen(false)}>{t("BUTTON.CLOSE") || "Close"}</Button>
						<Button variant="contained" onClick={handleSubmit} disabled={submitting}>
							{submitting ? t("BUTTON.SAVING") || "Saving..." : t("BUTTON.SAVE")}
						</Button>
					</>
				}
			/>

			<ModalWrapper
				open={Boolean(detailDoc)}
				setOpen={(open) => {
					if (!open) setDetailDoc(null);
				}}
				onClose={() => setDetailDoc(null)}
				title={detailDoc?.title || t("LABELS.POLICY")}
				subtitle={
					detailDoc?.version_label
						? `${t("LABELS.VERSION")}: ${detailDoc.version_label}`
						: undefined
				}
				content={
					detailDoc ? (
						<Box display="flex" flexDirection="column" gap={1}>
							<Box display="flex" gap={1} flexWrap="wrap">
								<Chip label={detailDoc.doc_type} size="small" />
								<Chip
									label={detailDoc.is_active ? t("MENU_ITEMS.ACTIVE") : t("MENU_ITEMS.INACTIVE")}
									color={detailDoc.is_active ? "success" : "default"}
									size="small"
								/>
							</Box>
							{detailDoc.description_html ? (
								<Box
									sx={{
										 maxHeight: "60vh",
										overflowY: "auto",
										overflowX: "auto",
										"& p": { mb: 1 } }}
									dangerouslySetInnerHTML={{ __html: detailDoc.description_html }}
								/>
							) : (
								<Typography variant="body2">
									{detailDoc.description_text || t("UI_TEXT.NO_DESCRIPTION_AVAILABLE")}
								</Typography>
							)}
							{detailDoc.external_url && (
								<Button
									variant="outlined"
									size="small"
									onClick={() => window.open(detailDoc.external_url, "_blank")}
								>
									{t("BUTTON.OPEN_LINK")}
								</Button>
							)}
							{detailDoc.file_path && (
								<Button
									variant="outlined"
									size="small"
									onClick={() =>
										window.open(`${ASSETS_BASE_URL}/${detailDoc.file_path}`, "_blank")
									}
								>
									{t("BUTTON.VIEW_FILE")}
								</Button>
							)}
						</Box>
					) : null
				}
				actions={
					<Button onClick={() => setDetailDoc(null)}>{t("BUTTON.CLOSE") || "Close"}</Button>
				}
			/>
		</Box>
	);
};

export default PolicyCenter;